import type { AxiosError } from 'axios';
import type { TFunction } from 'i18next';

export type ErrorCode =
  | 'VALIDATION'
  | 'AUTH'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'CONFLICT'
  | 'RATE_LIMIT'
  | 'SERVER'
  | 'NETWORK'
  | 'UNKNOWN';

/**
 * One entry of `ValidationErrorResponse.fieldErrors` — the per-field detail a
 * Bean-Validation 422 carries alongside its generic envelope message.
 *
 * `message` is rendered to users; `field` is NOT. It is the DTO property name
 * (`currency`, `dueDate`), i.e. developer chrome, and turning it into a human
 * label would need a per-field i18n table the backend has no contract for.
 */
export type FieldError = {
  field: string;
  message: string;
};

export type DomainError = {
  code: ErrorCode;
  /** i18n key — never user-facing message text directly */
  messageKey: string;
  /** Server-supplied message, only for developer console + bug reports — never rendered */
  rawMessage?: string;
  /**
   * Machine-readable refusal code from `ErrorResponse.code` (e.g. `EXPIRED`).
   * Only a handful of backend handlers publish one; `undefined` everywhere else.
   *
   * It exists so a caller can branch on WHY a request was refused without
   * matching `rawMessage`, which the backend localizes into the client's own
   * language. Callers MUST treat an unrecognised (or absent) value as "don't
   * know" and keep their existing behaviour — never as a terminal state.
   */
  serverCode?: string;
  /**
   * Seconds until the caller may retry, from `ErrorResponse.retryAfterSeconds`.
   * Present only on refusals that publish one (currently `LOGIN_COOLDOWN`).
   *
   * It travels in the BODY rather than the `Retry-After` header for two reasons:
   * the refusal is a 400, where `Retry-After` is not defined (RFC 9110 scopes it
   * to 503/429/3xx), and every consumer of an error in this app reads
   * `response.data` — nothing plumbs headers, so a header would be a second
   * channel for one field. Same precedent as `code`: an optional, omitted-when-
   * absent field on the standard error envelope.
   *
   * A caller MUST cope with it being absent (an older backend, a proxy that
   * rewrote the body) and never render an arithmetic result of `undefined`.
   */
  retryAfterSeconds?: number;
  /**
   * Per-field detail from `ValidationErrorResponse.fieldErrors`, on 422 only.
   *
   * `GlobalExceptionHandler.handleValidation` renders every `MethodArgumentNotValid`
   * field error through Hibernate Validator's own bundle, in the request's locale —
   * so these sentences arrive already translated (de-CH: "darf nicht leer sein"),
   * while the envelope's `message` is the fixed English string "Validation failed".
   * Reading only the envelope, as this module used to, threw the translated half away.
   *
   * Absent (never `[]`) when the refusal carried none: a 422 raised as
   * `UnprocessableContentException`, a global rather than field-level constraint,
   * or a body axios handed back as a Blob.
   */
  fieldErrors?: FieldError[];
};

/**
 * `fieldErrors` off an arbitrary response body, or `undefined`.
 *
 * Defensive because `response.data` is not always the JSON envelope: a blob-typed
 * request (BackupSection) gets a `Blob`, a proxy error page gets a string. Entries
 * with a blank or non-string message are dropped rather than rendered as "".
 */
function readFieldErrors(data: unknown): FieldError[] | undefined {
  if (!data || typeof data !== 'object') return undefined;
  const raw = (data as { fieldErrors?: unknown }).fieldErrors;
  if (!Array.isArray(raw)) return undefined;
  const parsed = raw.filter(
    (entry): entry is FieldError =>
      !!entry &&
      typeof entry === 'object' &&
      typeof (entry as FieldError).field === 'string' &&
      typeof (entry as FieldError).message === 'string' &&
      (entry as FieldError).message.trim().length > 0,
  );
  return parsed.length > 0 ? parsed : undefined;
}

export function mapAxiosError(err: unknown): DomainError {
  if (!err || typeof err !== 'object') {
    return { code: 'UNKNOWN', messageKey: 'errors.unknown' };
  }
  const axErr = err as AxiosError<{
    message?: string;
    code?: string;
    retryAfterSeconds?: number;
    fieldErrors?: FieldError[];
  }>;
  if (!axErr.response) {
    return { code: 'NETWORK', messageKey: 'errors.network' };
  }
  const status = axErr.response.status;
  const rawMessage = axErr.response.data?.message;
  const serverCode = axErr.response.data?.code;
  const rawRetry = axErr.response.data?.retryAfterSeconds;
  const retryAfterSeconds =
    typeof rawRetry === 'number' && Number.isFinite(rawRetry) && rawRetry > 0 ? rawRetry : undefined;

  if (status === 400) return { code: 'VALIDATION', messageKey: 'errors.validation', rawMessage, serverCode, retryAfterSeconds };
  if (status === 401) return { code: 'AUTH', messageKey: 'errors.auth', rawMessage, serverCode, retryAfterSeconds };
  if (status === 403) return { code: 'FORBIDDEN', messageKey: 'errors.forbidden', rawMessage, serverCode, retryAfterSeconds };
  if (status === 404) return { code: 'NOT_FOUND', messageKey: 'errors.notFound', rawMessage, serverCode, retryAfterSeconds };
  if (status === 409) return { code: 'CONFLICT', messageKey: 'errors.conflict', rawMessage, serverCode, retryAfterSeconds };
  // 422 is this app's validation status, not 400 — `GlobalExceptionHandler` answers
  // every `MethodArgumentNotValidException` with UNPROCESSABLE_ENTITY. Without this
  // branch it fell through to UNKNOWN, so the commonest refusal in the product was
  // classified as "no idea" and rendered as the generic unknown copy.
  if (status === 422) return { code: 'VALIDATION', messageKey: 'errors.validation', rawMessage, serverCode, retryAfterSeconds, fieldErrors: readFieldErrors(axErr.response.data) };
  if (status === 429) return { code: 'RATE_LIMIT', messageKey: 'errors.rateLimit', rawMessage, serverCode, retryAfterSeconds };
  if (status >= 500) return { code: 'SERVER', messageKey: 'errors.server', rawMessage, serverCode, retryAfterSeconds };
  return { code: 'UNKNOWN', messageKey: 'errors.unknown', rawMessage, serverCode, retryAfterSeconds };
}

/**
 * Did this 409 say "the record changed under you" (a stale `version`), as opposed to a business
 * conflict such as a duplicate name?
 *
 * Two server shapes mean it: `ConflictException`'s own sentence (the manual version check — it also
 * carries `currentVersion`) and the optimistic-lock handler's, which is the same sentence. Business
 * conflicts ("A template named 'x' already exists.") carry neither and keep their own copy.
 * Editors use it to put the field back to the stored value, not only to word the toast (F30).
 */
export function isStaleVersionConflict(err: unknown): boolean {
  if (mapAxiosError(err).code !== 'CONFLICT') return false;
  const data = (err as AxiosError<{ message?: string; code?: string; currentVersion?: unknown }>).response?.data;
  // The server's own signal (ConflictResponse.code) first; the older shapes stay as fallbacks.
  if (data?.code === 'STALE_VERSION') return true;
  if (typeof data?.currentVersion === 'number') return true;
  return typeof data?.message === 'string' && /modified by another user/i.test(data.message);
}

/**
 * Whole minutes a caller still has to wait, rounded UP so the number is never an
 * undersell — 27m01s reads as "28 minutes", and the last partial minute reads as
 * "1 minute" rather than "0". `undefined` when the refusal carried no retry time,
 * which callers must render as a timeless sentence rather than "NaN minutes".
 */
export function retryAfterMinutes(err: DomainError): number | undefined {
  const seconds = err.retryAfterSeconds;
  if (seconds === undefined) return undefined;
  return Math.max(1, Math.ceil(seconds / 60));
}

/**
 * Refusal codes whose `message` is NOT client copy, mapped to the i18n key that is.
 *
 * The rawMessage heuristic below assumes a backend sentence is user-safe unless it
 * looks like the axios template. That holds for the handlers that localize their
 * message into the client's own language — but not for every refusal. `RateLimitFilter`
 * runs before any controller, so it has no locale to render in and emits a fixed
 * English "Rate limit exceeded. Try again in N seconds."; without this table a
 * German-locale visitor read that sentence verbatim on the public inquiry and booking
 * forms.
 *
 * Keys are resolved against whatever namespace the caller's `t` is bound to, so only
 * keys that exist in EVERY namespace passed to this function belong here — `errors.*`
 * is duplicated across `common`, `booking` and `auth2` precisely for that. A refusal
 * needing feature-specific copy branches on `mapAxiosError(e).serverCode` in the page
 * instead (see OfferPublicViewContent), which is the same signal, read locally.
 */
const SERVER_CODE_MESSAGE_KEYS: Readonly<Record<string, string>> = {
  RATE_LIMIT_EXCEEDED: 'errors.rateLimit',
};

/**
 * Backend `message` values that are envelope boilerplate rather than client copy,
 * and so must never win over the localized fallback.
 *
 * `handleValidation` stamps the fixed English "Validation failed" on EVERY
 * Bean-Validation 422 — it names no field, says nothing actionable, and reached
 * German, French, Italian, Spanish and Portuguese studio members verbatim because
 * the rawMessage heuristic only recognised the axios template. It is matched
 * exactly: a backend sentence that merely starts with those words is somebody's
 * real copy and still renders.
 *
 * `handleNoResource` likewise stamps a fixed English "The requested resource was not
 * found" on an unmapped path (it used to echo Spring's internal sentence, route and
 * all — T21). Listed here so `errors.notFound` renders instead, which says the same
 * thing in the reader's own language.
 *
 * `handleUnexpected` (the catch-all) and `handleMissingPathVariable` stamp "An unexpected error
 * occurred" on every 500, which rendered verbatim — in English, on a German client's checkout
 * or accept page (R2-12). It lives on the backend as `GlobalExceptionHandler
 * .GENERIC_SERVER_ERROR_MESSAGE`; a test pins that the two agree.
 *
 * Deliberately NOT a general "skip anything English" rule. Handlers that localize
 * (`UnprocessableContentException`, `ConflictException`) send real client copy on
 * these same statuses and must keep winning — that is the T10 path. Nor is it a
 * blanket "ignore every 404 message": `GET /clients/999999` answers "Client with id
 * 999999 not found", which is real, specific copy the user should see.
 */
const ENVELOPE_BOILERPLATE_MESSAGES: ReadonlySet<string> = new Set([
  'Validation failed',
  'The requested resource was not found',
  'An unexpected error occurred',
]);

/** How many field complaints one toast may carry before it stops being readable. */
const MAX_RENDERED_FIELD_ERRORS = 3;

/**
 * Message shapes that are internal plumbing, never client copy. Matching one means
 * the localized fallback wins instead.
 *
 * CLAUDE.md's "Errors to users" hard rule: never leak `error.message` from axios,
 * stack traces, or status codes into user-facing UI. These are the shapes that
 * carry exactly that.
 *
 *  - the axios template ("Request failed with status code 404") — a status code
 *    read out loud, which is what the rule names first;
 *  - Spring's `NoResourceFoundException` sentence, which `handleNoResource` used to
 *    echo verbatim: "No static resource api/v1/settings/x for request
 *    '/api/v1/settings/x'." — framework jargon plus the internal route;
 *  - anything quoting an API path at all. That is the general form of the leak and
 *    the one that survives a Spring upgrade rewording its own message: no sentence
 *    a studio member should read has `/api/v1/` in it.
 *
 * The backend no longer sends the second shape (`GlobalExceptionHandler` returns a
 * fixed "Not Found"), so these are belt-and-braces — a stale bundle talking to a
 * new backend, or any future handler that echoes an exception message, still
 * cannot put a route in a toast.
 *
 * Deliberately narrow. `GET /api/v1/clients/999999` answers "Client with id 999999
 * not found", which IS real client copy on this same status and must keep rendering.
 */
const INTERNAL_MESSAGE_SHAPES: readonly RegExp[] = [
  /^Request failed with status code/,
  /^No static resource\b/,
  /\/api\/v\d+\//,
];

function isUserFacingMessage(message: string): boolean {
  if (INTERNAL_MESSAGE_SHAPES.some((shape) => shape.test(message))) return false;
  return !ENVELOPE_BOILERPLATE_MESSAGES.has(message.trim());
}

/**
 * The field complaints as one sentence, or `undefined` if there are none.
 *
 * Deduplicated because a form failing three `@NotNull`s yields the same sentence
 * three times, and capped because a toast is not a form. The field names are
 * omitted on purpose (see `FieldError`): showing every message beats showing only
 * the first, whose position in the list is DTO declaration order and has nothing
 * to do with which field the user actually got wrong.
 */
function fieldErrorSentence(fieldErrors: FieldError[] | undefined): string | undefined {
  if (!fieldErrors?.length) return undefined;
  const distinct = [...new Set(fieldErrors.map((fe) => fe.message.trim()))];
  return distinct.slice(0, MAX_RENDERED_FIELD_ERRORS).join(' · ');
}

/**
 * Render a user-facing toast/message from an error.
 * Precedence, most specific first:
 *  1. a recognised `ErrorResponse.code` — refusals whose backend message is known
 *     not to be client-facing copy;
 *  2. the per-field validation detail, which the backend already localized into the
 *     caller's own language and which names what to change;
 *  3. a backend-supplied message that appears to be user-safe — not internal
 *     plumbing (the axios template, a framework sentence, anything quoting an API
 *     path) and not envelope boilerplate;
 *  4. the generic i18n message for the error code — or `fallbackKey`, when the
 *     caller has copy of its own that says more than "something went wrong".
 *
 * `fallbackKey` exists so a screen can route its errors through this function
 * WITHOUT giving up the sentence it already had. Before it, a caller with a
 * specific fallback ("Could not save settings.") had to choose between its own
 * copy and the backend's localized detail, and several chose their own — which is
 * how the detail stayed invisible on those screens (T20). It only ever replaces
 * step 4; a real message from the backend still wins over it.
 */
export function errorToUserMessage(err: unknown, t: TFunction, fallbackKey?: string): string {
  const domainErr = mapAxiosError(err);
  // A stale save says WHO the problem is ("someone changed this") and what to do, in the reader's
  // language — not the English optimistic-lock sentence and not the caller's "Could not save" (F30).
  if (isStaleVersionConflict(err)) {
    return t('common:errors.staleVersion');
  }
  const codeKey = domainErr.serverCode
    ? SERVER_CODE_MESSAGE_KEYS[domainErr.serverCode]
    : undefined;
  if (codeKey) {
    return t(codeKey);
  }
  const fieldDetail = fieldErrorSentence(domainErr.fieldErrors);
  if (fieldDetail) {
    return fieldDetail;
  }
  if (domainErr.rawMessage && isUserFacingMessage(domainErr.rawMessage)) {
    return domainErr.rawMessage;
  }
  return t(fallbackKey ?? domainErr.messageKey);
}
/**
 * The server's message for ONE field of a 422, or `undefined`.
 *
 * `errorToUserMessage` flattens every field complaint into a toast, which is right for a form
 * that has no place to put them. A form that does — an amount next to its input, a date next to
 * its picker — asks here instead, so "A payment date cannot be in the future" sits under the
 * date and the person sees which field to change (RB-30a: a rejected save is never a generic toast).
 * `fields` are DTO property names (`netAmount`, `paidAt`); the first one present wins.
 */
export function fieldErrorFor(err: unknown, ...fields: string[]): string | undefined {
  const fieldErrors = mapAxiosError(err).fieldErrors;
  if (!fieldErrors) return undefined;
  for (const field of fields) {
    const hit = fieldErrors.find((fe) => fe.field === field);
    if (hit) return hit.message.trim();
  }
  return undefined;
}
